import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit2, Trash2, Printer, ChevronUp, ChevronDown, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const GenericSetupPage = ({ 
  title, 
  columns = [], 
  initialData = [],
  addButtonText = 'Add New',
  apiEndpoint = null,
  formFields = null
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEntries, setShowEntries] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    if (apiEndpoint) {
      try {
        const response = await api.get(apiEndpoint);
        if (response.data.success && response.data.data?.length > 0) {
          setData(response.data.data);
        } else {
          setData(initialData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setData(initialData);
      }
    } else {
      setData(initialData);
    }
    setLoading(false);
  };

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const filteredData = data
    .filter(row => {
      if (!searchQuery) return true;
      return columns.some(col => {
        const value = row[col.key];
        return value && value.toString().toLowerCase().includes(searchQuery.toLowerCase());
      });
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortDirection === 'asc' ? comparison : -comparison;
    })
    .slice(0, showEntries);

  const handleAdd = () => {
    setEditingItem(null);
    const emptyForm = {};
    columns.forEach(col => {
      emptyForm[col.key] = '';
    });
    if (formFields) {
      formFields.forEach(field => {
        emptyForm[field.key] = field.defaultValue || '';
      });
    }
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    if (apiEndpoint) {
      try {
        await api.delete(`${apiEndpoint}/${id}`);
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
    setData(prev => prev.filter(item => item.id !== id));
    toast.success('Record deleted');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (apiEndpoint) {
      try {
        if (editingItem) {
          await api.put(`${apiEndpoint}/${editingItem.id}`, formData);
          setData(prev => prev.map(item => item.id === editingItem.id ? { ...item, ...formData } : item));
          toast.success('Record updated successfully');
        } else {
          const response = await api.post(apiEndpoint, formData);
          const newItem = { ...formData, id: response.data?.id || Date.now().toString() };
          setData(prev => [...prev, newItem]);
          toast.success('Record created successfully');
        }
      } catch (error) {
        // Fallback to local update
        if (editingItem) {
          setData(prev => prev.map(item => item.id === editingItem.id ? { ...item, ...formData } : item));
          toast.success('Record updated successfully');
        } else {
          const newItem = { ...formData, id: Date.now().toString() };
          setData(prev => [...prev, newItem]);
          toast.success('Record created successfully');
        }
      }
    } else {
      if (editingItem) {
        setData(prev => prev.map(item => item.id === editingItem.id ? { ...item, ...formData } : item));
        toast.success('Record updated successfully');
      } else {
        const newItem = { ...formData, id: Date.now().toString() };
        setData(prev => [...prev, newItem]);
        toast.success('Record created successfully');
      }
    }
    
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const handlePrint = () => {
    window.print();
  };

  const renderFormFields = () => {
    const fields = formFields || columns.map(col => ({
      key: col.key,
      label: col.label,
      type: 'text',
      required: true
    }));

    return fields.map(field => (
      <div key={field.key} className={field.fullWidth ? 'col-span-2' : ''}>
        <Label htmlFor={field.key}>{field.label} {field.required && '*'}</Label>
        {field.type === 'select' ? (
          <select
            id={field.key}
            value={formData[field.key] || ''}
            onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={field.required}
          >
            <option value="">Select...</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            id={field.key}
            value={formData[field.key] || ''}
            onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            required={field.required}
          />
        ) : (
          <Input
            id={field.key}
            type={field.type || 'text'}
            value={formData[field.key] || ''}
            onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
            className="text-sm"
            required={field.required}
          />
        )}
      </div>
    ));
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid={`setup-page-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        {/* Section Header - Alora Plus Style */}
        <div className="bg-white border border-gray-200 rounded">
          {/* Title Bar */}
          <div 
            className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
            <div className="flex items-center gap-2">
              <button 
                onClick={(e) => { e.stopPropagation(); handlePrint(); }}
                className="p-1 hover:bg-gray-200 rounded"
                title="Print"
              >
                <Printer className="w-4 h-4 text-gray-500" />
              </button>
              {isCollapsed ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              )}
            </div>
          </div>

          {!isCollapsed && (
            <div className="p-4">
              {/* Controls Row - Alora Plus Style */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                    onClick={handleAdd}
                    data-testid="add-new-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {addButtonText}
                  </Button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Search:</span>
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-40 h-8 text-sm"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Show</span>
                    <select
                      value={showEntries}
                      onChange={(e) => setShowEntries(Number(e.target.value))}
                      className="h-8 px-2 border border-gray-300 rounded text-sm"
                      data-testid="show-entries"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-gray-600">entries</span>
                  </div>
                </div>
              </div>

              {/* Data Table - Alora Plus Style */}
              <div className="border border-gray-200 rounded overflow-x-auto">
                <table className="w-full text-sm" data-testid="data-table">
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-20">
                        Action
                      </th>
                      {columns.map((col) => (
                        <th 
                          key={col.key}
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSort(col.key)}
                        >
                          <div className="flex items-center gap-1">
                            {col.label}
                            <div className="flex flex-col">
                              <ChevronUp className={`w-3 h-3 -mb-1 ${sortColumn === col.key && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} />
                              <ChevronDown className={`w-3 h-3 -mt-1 ${sortColumn === col.key && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} />
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-gray-500">
                          No data available in table
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((row, index) => (
                        <tr 
                          key={row.id || index} 
                          className="border-b border-gray-100 hover:bg-blue-50"
                          data-testid={`table-row-${index}`}
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEdit(row)}
                                className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-blue-600"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(row.id)}
                                className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          {columns.map((col) => (
                            <td key={col.key} className="px-3 py-2 text-gray-700">
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Info */}
              <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                <div>
                  Showing {filteredData.length > 0 ? 1 : 0} to {filteredData.length} of {data.length} entries
                </div>
                <div className="flex items-center gap-1">
                  <button className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50" disabled>
                    Previous
                  </button>
                  <button className="px-3 py-1 bg-blue-600 text-white rounded">1</button>
                  <button className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50" disabled>
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto" data-testid="edit-modal">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">
                  {editingItem ? `Edit ${title}` : `Add ${title}`}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  {renderFormFields()}
                </div>
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="save-btn">
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GenericSetupPage;
